import React, {useState, useContext, ReactElement} from 'react';
import {useQuery} from "@apollo/client";
import {convertFieldsToGraphqlVariables, graphqlFindResultsWrapped} from "../graphql-helpers";
import QueryString from "query-string";
import {useHref, useLocation, useMatch, useRoutes} from "react-router";
import {ToolbarContext, ToolBarPrimaryProps} from "../contexts/toolbar-context";
import {useCallback, useEffect} from 'react';
import FieldTable from './field-table';
import {useUserContext} from '../contexts/user-context';
import {Outlet, useNavigate, useParams} from "react-router-dom";
import {FieldTableColumnProps, IdedType} from "./FieldTable/FieldTableTypes";
import {ModalWindow} from './ModalWindow';
import {Cancel, ChevronRight, Delete} from "@mui/icons-material";
import {Alert, Button, ButtonGroup, IconButton} from '@mui/material';
import { useToast } from "../contexts/toast";

type SideListProps<Type extends IdedType> = {
    searchForm?: any,
    mode?: string | null
    query: any,
    baseUrl: string,
    label: string,
    deleteMutation?: any,
    columns: FieldTableColumnProps<Type>[],
    permission?: string
    modes?: any[],
    className?:string,
    noEditorScroll?:boolean,
    hasDelete?:boolean,
    footer?:any,
    isDigitalSearch?: boolean,
    extraContent?:(any)=>ReactElement,
    fixedVariables?: any,
    simpleDelete?: boolean,
    skipScroll?: boolean,
    getResults?: any
}


export function SideList<Type extends IdedType>({footer,searchForm,mode,deleteMutation,columns,query,baseUrl,isDigitalSearch,label,permission,modes,className,noEditorScroll,hasDelete=true,extraContent,fixedVariables}:SideListProps<Type>): ReactElement {
    const [activeMode, setActiveMode] = useState(mode ?mode : 'default');
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const user = useUserContext();
    const {toast, dismiss} = useToast();

    const [isSearchPanel, setIsSearchPanel] = useState(searchForm && !params.id && !location.search);
    //const [, setExclusions] = useState([]);
    //const [, setExclusionIds] = useState([]);
    const [deleteId, setDeleteId] = useState(0);
    const [doDeleteMutation] = deleteMutation??[];
    const [parsedQueryString, setParsedQueryString] = useState(null);
    const toolbarContext = useContext(ToolbarContext);
    const {data, loading, error, refetch} = useQuery(query, {
        variables: {...fixedVariables, ...convertFieldsToGraphqlVariables(parsedQueryString)},
        fetchPolicy: "network-only",
        skip: searchForm && (!parsedQueryString || !parsedQueryString.length)
    });

    // convert url parameters to searchform parameters
    useEffect(() => {
        const rawParsedQueryString = QueryString.parse(location.search);
        const queryStringArray = [];
        Object.entries(rawParsedQueryString).forEach(function ([k, v]) {
            const name = k.split('-');
            queryStringArray.push({name: name[0], type: name[1], value: v});
        });
        // Ensure digital_search is included in the parsed query string
        if (isDigitalSearch && (rawParsedQueryString['name-like'] || rawParsedQueryString['regionId-in'])) {
            queryStringArray.push({ name: 'digital_search', type: 'default', value: true });
        }
        setParsedQueryString(queryStringArray);
        console.log('[SL]', 'setQueryString to', queryStringArray);
    }, [location, isDigitalSearch]);

    // convert query string from search form to URL parameterss
    const setQueryString = useCallback((newQs = null) => {
        if (!newQs) {
            newQs = parsedQueryString;
        }
        const qs = {};
        if (newQs) {
            newQs?.forEach((val) => {
                qs[val.name + '-' + val.type] = val.value;
            });
        }
        navigate(location.pathname + '?' + QueryString.stringify(qs));
    }, [navigate, location.pathname, parsedQueryString]);

    // do the search
    const doSearch = useCallback((val) => {
        setIsSearchPanel(false);
        // replace the query string with the search parameters
        //    this.parsedQueryString = val;
        //setExclusionIds([]);
        //setExclusions([]);
        setQueryString(val);
    }, [setQueryString]);

    const newObject = useCallback(() => {
        navigate(baseUrl + 'new' + (location.search ? location.search : ''));
    }, [baseUrl, location.search]);

    const doCancelSearch = useCallback(() => {
        setIsSearchPanel(false);
    }, []);
    const showPanel = useCallback(() => {
        console.log('searchpanel?');
        setIsSearchPanel(true);
    }, []);

    // why is this here? o_O
    /*  useEffect(()=> {
        setQueryString();
      },[]);*/
    // why is this not all in use

    const isEditing = !!params.id;
    const hasResults = !searchForm || (parsedQueryString && (parsedQueryString.length > 0));


    useEffect(() => {
//        toolbarContext.set((currentToolbar) => {
        const baseToolbar:ToolBarPrimaryProps = {
            primary: [
                {
                    type: 'button',
                    label: 'Search',
                    tooltip: 'Search',
                    icon: 'search',
                    callback: showPanel
                }
            ],
            title: label + ' Search',
            primaryUrl: baseUrl,
            secondaryUrl: params.id
        };
        if(permission && user.hasPermission(permission + '.create')) {
                baseToolbar.primary.push({
                    type: 'button',
                    label: 'New',
                    tooltip: 'New ' + label,
                    icon: 'file-add',
                    callback: newObject
                });
        }
        if (modes) {
            modes.forEach((mode) => {
                baseToolbar.primary.unshift({
                    type: 'button', label: '', tooltip: mode.label, icon: mode.icon, callback: () => {
                        setActiveMode(mode.name);
                    }
                });
            })
        }
        if (!searchForm) {
            baseToolbar.primary.shift();
            baseToolbar.title = label;
        }
        baseToolbar.primaryUrl = baseUrl;
        baseToolbar.secondaryUrl = params.id;
        toolbarContext.setPrimary(baseToolbar);
    }, [isEditing, user, setActiveMode, newObject, showPanel]);

    const source = (!loading && !error && data) ? graphqlFindResultsWrapped(data) : {
        response: {results: []},
        key: 'results'
    };
    const onDelete = (id) => {
        setDeleteId(id);
    };
    const clearDeleteId = () => {
        setDeleteId(0);
    };
    const doDelete = async () => {
        toast('Deleting row', {id: 'DEL_ROW'});
        try {
            await doDeleteMutation({variables: {input: {delete: [deleteId]}}});
            toast('Row deleted', {id: 'DEL_ROW', variant: 'success'});
            await refetch();
            setDeleteId(0);
        } catch (e) {
            toast('Row deletion failed', {id: 'DEL_ROW', variant: 'error'});
            setDeleteId(0);
        }
    };
    const closeEditor = useCallback(() => {
        navigate(baseUrl + location.search);
    }, [baseUrl, location.search]);
    const content = React.useMemo(()=>{
        console.log('rendering sidelist AGAIN?',[source && source?.response, activeMode, className, hasDelete,  permission, loading, columns, modes]);
        if (source) {
            if (activeMode == 'default') {
                return <FieldTable className={className + '-list'} stickyFooter={true} footer={footer}
                                    hasDelete={hasDelete && user.hasPermission(permission + '.delete')}
                                    loading={loading} name={source.key} doDelete={onDelete}
                                    values={source.response[source.key]} columns={columns}/>
            } else {
                let toReturn = null;
                modes.forEach((v) => {
                    if (v.name == activeMode) {
                        toReturn =  v.render(source.response[source.key]);
                    }
                });
                return toReturn;
            }
        }
    },[source && source?.response, activeMode, className, hasDelete,  permission, loading, modes]);
    return (
        <div
            className={'side-editor ' + (className ? className : '') + (hasResults ? ' has-results' : '') + (isEditing ? ' is-editing' : '') + (' mode-' + activeMode)}>

            {extraContent?.(source ? source.response[source.key] : null) ?? null}
            <div className="side-editor__list is-scrollable">
                {isSearchPanel && searchForm({
                    label: label,
                    onSearch: doSearch,
                    params: parsedQueryString,
                    isOpen: isSearchPanel,
                    onCancel: doCancelSearch
                })}
                {source && content}
            </div>
            <div className="side-editor__separator" style={{display: isEditing ? '' : 'none'}}>
                <IconButton size="large" onClick={closeEditor}><ChevronRight/></IconButton></div>
            <div className={'side-editor__editor' + (noEditorScroll ? '' : ' is-scrollable')}>
                <Outlet/>
            </div>
            <ModalWindow isOpen={!!deleteId} onClose={clearDeleteId}
                         header="Confirm Deletion"
                         actions={<ButtonGroup>
                             <Button key="d1" onClick={doDelete} startIcon={<Delete/>}>Delete this row</Button>
                             <Button key="d2" onClick={clearDeleteId} startIcon={<Cancel/>}>Cancel</Button>
                         </ButtonGroup>
                         }
            ><Alert severity="warning">Delete this record?</Alert></ModalWindow>
        </div>);
}
